import { FC, useCallback, useState, useMemo, useRef, Fragment } from 'react'
import { useRouter } from 'next/router'
import { Row, Col, Tag, Icon, HorizontalScroll, Image } from '@/client/components'
import { useWindowSize } from '@/client/hooks'
import { onlyUnique } from './helpers'
import * as S from './styles'
import { MaterialProps, IMaterial } from '../types'

export const MaterialList: FC<MaterialProps> = ({ materials, download }) => {
  const { locale } = useRouter()
  const { breakpoint } = useWindowSize()
  const [selectedType, setSelectedType] = useState<string>('')

  const filter = useMemo(() => {
    const filteredTypes: IMaterial[] = materials.filter(onlyUnique)

    return filteredTypes.map(({ type, title }) => (
      <Tag key={type} size='large' kind='filter' action={() => setSelectedType(type)} isActive={type === selectedType}>
        {type || title}
      </Tag>
    ))
  }, [materials, locale, selectedType])

  const DraggableImage: React.FC<{ src: string; alt: string; width: number; height: number }> = ({
    src,
    alt,
    width,
    height
  }) => {
    const wrapperRef = useRef<HTMLDivElement | null>(null)
    const imageRef = useRef<HTMLImageElement | null>(null)
    const [isDragging, setIsDragging] = useState(false)
    const [position, setPosition] = useState({ x: 0, y: 0 })
    const [start, setStart] = useState({ x: 0, y: 0 })

    const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
      setIsDragging(true)
      setStart({ x: e.clientX - position.x, y: e.clientY - position.y })
      document.body.style.userSelect = 'none' // Disable text selection
    }

    const onMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isDragging || !wrapperRef.current || !imageRef.current) return

      const wrapper = wrapperRef.current.getBoundingClientRect()
      const image = imageRef.current.getBoundingClientRect()

      // Calculate max/min boundaries
      const minX = wrapper.width - image.width // Maximum left limit (negative)
      const minY = wrapper.height - image.height // Maximum top limit (negative)

      const newX = Math.min(0, Math.max(minX, e.clientX - start.x))
      const newY = Math.min(0, Math.max(minY, e.clientY - start.y))

      setPosition({ x: newX, y: newY })
    }

    const onMouseUp = () => {
      setIsDragging(false)
      document.body.style.userSelect = 'auto' // Re-enable text selection
    }

    // Reset position when mouse leaves
    const onMouseLeave = () => {
      setPosition({ x: 0, y: 0 })
    }

    return (
      <S.ImageWrapper
        ref={wrapperRef}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseLeave}
      >
        <S.InnerWrapper>
          <Image
            ref={imageRef}
            src={src}
            alt={alt}
            width={width}
            height={height}
            style={{
              display: 'block',
              width: '100%',
              height: 'auto',
              position: 'relative',
              left: `${position.x}px`,
              top: `${position.y}px`,
              cursor: isDragging ? 'grabbing' : 'grab',
              pointerEvents: isDragging ? 'none' : 'auto',
              transform: isDragging ? 'scale(1.5)' : 'none'
            }}
          />
        </S.InnerWrapper>
      </S.ImageWrapper>
    )
  }

  const labelList = useCallback(() => {
    let currMaterials = materials

    if (selectedType) {
      currMaterials = materials.filter(({ type }) => type === selectedType)
    }

    return currMaterials.map(({ image, title, link }) => (
      <Fragment key={`key_${title}`}>
        {image && link && title && (
          <S.Material key={title}>
            <S.MaterialImage>
              <DraggableImage src={image.url} alt={image.alt} width={image.width} height={image.height} />
            </S.MaterialImage>
            <S.Text>{title}</S.Text>
            <S.Button href={link} target='_blank'>
              <Icon name='pdf' />
              {download}
            </S.Button>
          </S.Material>
        )}
      </Fragment>
    ))
  }, [materials, selectedType])

  return (
    <>
      <Row>
        <Col xs={12}>
          <S.Filter>
            {breakpoint !== 'desktop' && <HorizontalScroll>{filter}</HorizontalScroll>}
            {(breakpoint === 'desktop' || breakpoint === 'wide-desktop') && <div>{filter}</div>}
          </S.Filter>
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          {breakpoint !== 'desktop' && <HorizontalScroll>{labelList()}</HorizontalScroll>}
          {(breakpoint === 'desktop' || breakpoint === 'wide-desktop') && <S.Materials>{labelList()}</S.Materials>}
        </Col>
      </Row>
    </>
  )
}
